import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { resolveNavigation } from '@platform/entitlements';
import type { PortalModule } from '../../api';
import { ApplicationMasterDataTab } from '../master-data/ApplicationMasterDataTab';
import { CurrentRunRateTab } from '../master-data/CurrentRunRateTab';
import { ModulePlaceholder } from '../ModulePlaceholder';
import { SectionIndex } from '../SectionIndex';
import { AprStartPage } from './StartPage';
import { AprScreen } from './AprScreen';
import { APRProvider } from './compat/APRContext';
import { shouldSkipStart } from './skipStart';
import FrameworksList from './settings/FrameworksList';
import FrameworkBuilder from './settings/FrameworkBuilder';
import FrameworkDesigner from './settings/FrameworkDesigner';
import ScoringModel from './settings/ScoringModel';
import UserGuide from './settings/UserGuide';
import HelpSupport from './settings/HelpSupport';
import KnowledgeLibrary from './settings/KnowledgeLibrary';
import LicensingAnalysis from './settings/LicensingAnalysis';
import LicensingResults from './settings/LicensingResults';
import APRModel from './evaluation/APRModel';
import APRExecution from './evaluation/APRExecution';
import APRRationalizationModels from './evaluation/APRRationalizationModels';
import ApplicationPortfolioQA from './evaluation/ApplicationPortfolioQA';
import ApplicationPortfolioScoring from './evaluation/ApplicationPortfolioScoring';
import DataExportReports from './evaluation/DataExportReports';
import FinalApplicationDispositionModel from './evaluation/FinalApplicationDispositionModel';
import ScoringAuditTrail from './evaluation/ScoringAuditTrail';
import RunAnalysis from './ai-analysis/RunAnalysis';
import AnalysisResults from './ai-analysis/AnalysisResults';
import AnalysisRepository from './ai-analysis/AnalysisRepository';
import ReportLibrary from './ai-analysis/ReportLibrary';
import ExecutiveDashboard from './executive/ExecutiveDashboard';

/**
 * Application Portfolio Rationalization.
 *
 * Its screens are bucketed — today only PORTFOLIO, holding Applications and Cost,
 * which read the `apr` schema they were migrated from. They were briefly a module of
 * their own; they belong to the application whose estate they describe, so they moved
 * here on the owner's instruction. APR's own screens arrive as further buckets
 * alongside this one, some of them nesting a level deeper (Application Evaluation →
 * APR Model & Execution → APR Model).
 *
 * The route is a wildcard and the position is resolved by `resolveNavigation` against
 * the catalogue, so a bucket that nests deeper needs a catalogue entry and a screen —
 * never a new route. Buckets and sections are rendered by the drawer; a tab strip here
 * would be a second navigation model competing with the shell's, and the two would
 * disagree about where the user is.
 */
// Keyed by the section path below the module, so a screen is registered at exactly the
// place the catalogue puts it. Each draws its own PageHeader, because the header carries
// that screen's toolbar actions — a header here as well would stack two titles.
const SCREENS: Record<string, () => JSX.Element> = {
  'portfolio/applications': () => <ApplicationMasterDataTab />,
  /* Executive Dashboard — new work, not a port, so it draws its own chrome rather than
     rendering inside AprScreen's cloned source stylesheet. */
  'executive-dashboard': () => <ExecutiveDashboard />,
  'portfolio/cost': () => <CurrentRunRateTab />,
  // Settings — cloned from the source, so each renders inside AprScreen for the
  // source's own stylesheet.
  'settings/frameworks': () => (
    <AprScreen>
      <FrameworksList />
    </AprScreen>
  ),
  'settings/framework-builder': () => (
    <AprScreen>
      <FrameworkBuilder />
    </AprScreen>
  ),
  'settings/framework-designer': () => (
    <AprScreen>
      <FrameworkDesigner />
    </AprScreen>
  ),
  'settings/disposition-matrix': () => (
    <AprScreen>
      <ScoringModel />
    </AprScreen>
  ),
  // The source routes this as /ai-analysis/knowledge/:category; the category is the
  // section key here, so both land on the same screen with a different param.
  'knowledge-library/architecture-process-diagrams': () => (
    <AprScreen>
      <KnowledgeLibrary category="diagrams" />
    </AprScreen>
  ),
  'knowledge-library/licensing': () => (
    <AprScreen>
      <KnowledgeLibrary category="licensing" />
    </AprScreen>
  ),
  'license-intelligence/licensing-analysis': () => (
    <AprScreen>
      <LicensingAnalysis />
    </AprScreen>
  ),
  'license-intelligence/licensing-results': () => (
    <AprScreen>
      <LicensingResults />
    </AprScreen>
  ),
  // Application Evaluation — the deepest bucket, so these keys carry three segments.
  // The screens are cloned, so each renders inside AprScreen for the source's stylesheet.
  'application-evaluation/apr-model-execution/apr-model': () => (
    <AprScreen>
      <APRModel />
    </AprScreen>
  ),
  'application-evaluation/apr-model-execution/apr-execution': () => (
    <AprScreen>
      <APRExecution />
    </AprScreen>
  ),
  'application-evaluation/apr-model-execution/rationalization-models': () => (
    <AprScreen>
      <APRRationalizationModels />
    </AprScreen>
  ),
  'application-evaluation/initial-disposition/application-portfolio-q-a': () => (
    <AprScreen>
      <ApplicationPortfolioQA />
    </AprScreen>
  ),
  'application-evaluation/initial-disposition/initial-portfolio-scoring': () => (
    <AprScreen>
      <ApplicationPortfolioScoring />
    </AprScreen>
  ),
  'application-evaluation/initial-disposition/data-export-reports': () => (
    <AprScreen>
      <DataExportReports />
    </AprScreen>
  ),
  'application-evaluation/disposition-intelligence/run-analysis': () => (
    <AprScreen>
      <RunAnalysis />
    </AprScreen>
  ),
  'application-evaluation/disposition-intelligence/analysis-results': () => (
    <AprScreen>
      <AnalysisResults />
    </AprScreen>
  ),
  'application-evaluation/disposition-intelligence/analysis-repository': () => (
    <AprScreen>
      <AnalysisRepository />
    </AprScreen>
  ),
  'application-evaluation/disposition-intelligence/report-library': () => (
    <AprScreen>
      <ReportLibrary />
    </AprScreen>
  ),
  'application-evaluation/final-disposition': () => (
    <AprScreen>
      <FinalApplicationDispositionModel />
    </AprScreen>
  ),
  'application-evaluation/audit-trail': () => (
    <AprScreen>
      <ScoringAuditTrail />
    </AprScreen>
  ),
  'user-guide': () => (
    <AprScreen>
      <UserGuide />
    </AprScreen>
  ),
  'help-support': () => (
    <AprScreen>
      <HelpSupport />
    </AprScreen>
  ),
};

export interface AprModuleProps {
  modules: PortalModule[];
  /** Greets the user, as the source's start screen does. */
  firstName?: string;
}

export function AprModule({ modules, firstName }: AprModuleProps) {
  // `modules` holds only what this user may open, with sections already filtered to
  // what they may see. Anything else is a typed URL and is redirected rather than
  // rendering a screen whose API calls would be refused.
  const module = modules.find((m) => m.id === 'apr');
  if (!module || module.sections.length === 0) return <Navigate to="/" replace />;

  return <AprModuleContent modules={modules} module={module} firstName={firstName} />;
}

function AprModuleContent({
  modules,
  module,
  firstName,
}: {
  modules: PortalModule[];
  module: PortalModule;
  firstName?: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const at = resolveNavigation(location.pathname, [module]);

  // `/apr` is APR's own Start Menu, the screen the source shows at /start — not a list
  // of buckets. The buckets stay reachable from the left nav.
  //
  // Unless the user asked to skip it. This is where that preference is finally HONOURED:
  // the toggle on the start page has always written `apr.skipStart`, and until now
  // nothing anywhere read it, so the welcome screen appeared every time regardless.
  // The target is checked against the buckets this user can actually open, so a stale
  // or now-unlicensed choice falls through to the welcome screen rather than bouncing
  // them to a route the server would refuse.
  if (at.trail.length === 0) {
    const skipTo = shouldSkipStart(module.sections, location.search);
    if (skipTo) return <Navigate to={skipTo} replace />;
    return <AprStartPage firstName={firstName} sections={module.sections} />;
  }

  const deepest = at.trail[at.trail.length - 1]!;

  // Landed on a bucket rather than a screen — list what is inside it and let the user
  // pick, rather than choosing the first for them.
  if (!at.section) {
    return (
      <SectionIndex
        title={deepest.label}
        sections={deepest.sections ?? []}
        onOpen={(path) => navigate(path)}
      />
    );
  }

  const render = SCREENS[at.trail.map((s) => s.key).join('/')];
  if (!render) return (
      <ModulePlaceholder
        modules={modules}
        module={module}
        section={at.section ? { key: at.section.key, label: at.section.label } : undefined}
      />
    );

  // APR's own shared store. Four cloned screens — APR Model, APR Execution, Portfolio
  // Q&A and Portfolio Scoring — call `useAPR()`, which throws outside a provider.
  //
  // The source mounts it once at the app root, but only once authenticated
  // (App.tsx:425, `AuthenticatedAPRProvider`), because on mount it loads dimensions,
  // customers, applications, models, runs and run-rate in one pass. That gate is already
  // passed here — a screen only renders for a user holding an APR seat.
  //
  // It wraps the SCREEN rather than each screen wrapping itself, so React keeps one
  // provider instance across APR navigation: same component type in the same position,
  // so moving between APR screens reuses the loaded store instead of refetching all six.
  // (The Start page and bucket index above return earlier and need no store.)
  return <APRProvider>{render()}</APRProvider>;
}
